"use client"

import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react"
import { usePathname } from "next/navigation"

// Note: Setting value here to allow us to use it as a global singleton. We
// could store it as a state variable in the provider but then when updating
// the value it'd cause a re-render which we're trying to avoid so that on first
// render any page can synchronously determine navigation type
let isFirstLoad = true

type ContextValue = {
  hasNavigated: boolean
}

const NavigationContext = createContext<ContextValue | undefined>(undefined)

type ProviderProps = {
  children: ReactNode
}

export const NavigationProvider = ({ children }: ProviderProps) => {
  const pathname = usePathname()

  // We don't update this value after first render so we can safely use state
  // to store it
  const [pathnameOnLoad] = useState(pathname)

  // Here also not using 'useEffect' to avoid first state being 'null' - this
  // gives us a synchronous way to determine navigation type on first render
  const hasNavigated = useMemo(() => {
    if (pathname !== pathnameOnLoad) isFirstLoad = false
    return !isFirstLoad
  }, [pathname, pathnameOnLoad])

  const contextValue: ContextValue = useMemo(
    () => ({ hasNavigated }),
    [hasNavigated],
  )

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  )
}

/** Hook to access the 'NavigationProvider' context. */
export const useNavigation = () => {
  const contextValue = useContext<ContextValue | undefined>(NavigationContext)

  if (!contextValue) {
    throw new Error(
      "'useNavigation' must be used in a component with a parent 'NavigationProvider'.",
    )
  }

  return contextValue
}
